import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'src/routes/hooks';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import TablePagination from '@mui/material/TablePagination';
import CircularProgress from '@mui/material/CircularProgress';

import { DashboardContent } from 'src/layouts/dashboard';

import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';

import { createBranch, BranchStatus, type CreateBranchDto, type BranchWorkingHour } from 'src/api/branch_api_data';
import { companyApi } from 'src/api/company.api';
import { usersApi, type UserDto } from 'src/api/users.api';

import {
  WorkingHoursGrid,
  getDefaultWorkingHours,
  validateWorkingHours,
} from '../components/WorkingHoursGrid';

// ----------------------------------------------------------------------

export function BranchCreateEnhancedView() {
  const router = useRouter();

  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<{ id: string; name: string }[]>([]);
  const [employees, setEmployees] = useState<UserDto[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [employeePage, setEmployeePage] = useState(0);
  const [employeeRowsPerPage, setEmployeeRowsPerPage] = useState(5);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [employeeTotalElements, setEmployeeTotalElements] = useState(0);
  // BUG-4: "Sorumlu Çalışan Seç" modalı client-side arama (ad/kullanıcı adı).
  const [employeeSearch, setEmployeeSearch] = useState('');
  const filteredEmployees = employees.filter((u) => {
    const q = employeeSearch.trim().toLowerCase();
    if (!q) return true;
    return `${u.name ?? ''} ${u.username ?? ''}`.toLowerCase().includes(q);
  });

  const [formData, setFormData] = useState<CreateBranchDto>({
    name: '',
    latitude: 0,
    longitude: 0,
    branchOpeningTime: '09:00',
    branchClosingTime: '18:00',
    status: BranchStatus.OPEN,
    responsibleUsernames: [],
    companyId: '',
    address: '',
  });

  const [errors, setErrors] = useState<Partial<CreateBranchDto>>({});
  const [workingHours, setWorkingHours] = useState<BranchWorkingHour[]>(getDefaultWorkingHours());
  const [workingHoursError, setWorkingHoursError] = useState<string | null>(null);

  // Fetch companies
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const companiesData = await companyApi.getSimpleList();
        setCompanies(companiesData);
      } catch (error) {
        console.error('Failed to fetch companies:', error);
      }
    };

    fetchCompanies();
  }, []);

  // Fetch users for selection
  const fetchEmployees = useCallback(async () => {
    try {
      setEmployeeLoading(true);
      console.log('Fetching users for selection...');

      const data = await usersApi.getAll(employeePage, employeeRowsPerPage, 'name', 'asc');
      console.log('Users loaded:', data.content.length);
      setEmployees(data.content);
      setEmployeeTotalElements(data.totalElements);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setEmployees([]);
      setEmployeeTotalElements(0);
    } finally {
      setEmployeeLoading(false);
    }
  }, [employeePage, employeeRowsPerPage]);

  useEffect(() => {
    if (employeeDialogOpen) {
      fetchEmployees();
    }
  }, [employeeDialogOpen, fetchEmployees]);

  const handleInputChange = useCallback((field: keyof CreateBranchDto) => 
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = field === 'latitude' || field === 'longitude' 
        ? parseFloat(event.target.value) || 0 
        : event.target.value;
        
      setFormData(prev => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: undefined }));
      }
    }, [errors]
  );

  const handleSelectChange = useCallback((field: keyof CreateBranchDto) => 
    (event: any) => {
      setFormData(prev => ({ ...prev, [field]: event.target.value }));
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: undefined }));
      }
    }, [errors]
  );

  const handleEmployeeSelection = useCallback((username: string) => {
    setSelectedEmployees(prev => 
      prev.includes(username) 
        ? prev.filter(e => e !== username)
        : [...prev, username]
    );
  }, []);

  const handleSelectAllEmployees = useCallback((checked: boolean) => {
    if (checked) {
      const q = employeeSearch.trim().toLowerCase();
      const visible = employees.filter((u) =>
        !q || `${u.name ?? ''} ${u.username ?? ''}`.toLowerCase().includes(q));
      setSelectedEmployees(visible.map(emp => emp.username));
    } else {
      setSelectedEmployees([]);
    }
  }, [employees, employeeSearch]);

  const handleAddSelectedEmployees = useCallback(() => {
    const newResponsibles = selectedEmployees.filter(
      username => !formData.responsibleUsernames.includes(username)
    );

    setFormData(prev => ({
      ...prev,
      responsibleUsernames: [...prev.responsibleUsernames, ...newResponsibles]
    }));

    setEmployeeDialogOpen(false);
    setSelectedEmployees([]);
  }, [selectedEmployees, formData.responsibleUsernames]);

  const handleRemoveResponsible = useCallback((username: string) => {
    setFormData(prev => ({
      ...prev,
      responsibleUsernames: prev.responsibleUsernames.filter(e => e !== username)
    }));
  }, []);

  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<CreateBranchDto> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Şube adı gereklidir';
    }

    if (!formData.address?.trim()) {
      newErrors.address = 'Adres gereklidir';
    }

    if (!formData.companyId) {
      newErrors.companyId = 'Firma seçimi gereklidir';
    }

    if (formData.latitude === undefined || isNaN(formData.latitude) || formData.latitude < -90 || formData.latitude > 90) {
      newErrors.latitude = 'Geçersiz enlem değeri (-90 ile 90 arasında olmalıdır)' as any;
    }

    if (formData.longitude === undefined || isNaN(formData.longitude) || formData.longitude < -180 || formData.longitude > 180) {
      newErrors.longitude = 'Geçersiz boylam değeri (-180 ile 180 arasında olmalıdır)' as any;
    }

    // BUG-1: Sorumlu artık opsiyonel — yeni firma kendi personeli olmadan ilk şubesini
    // açabilsin (onboarding kilidi kaldırıldı). Yönetici sonradan "Yöneticiler" sekmesinden
    // atanır. Backend zaten 0-manager şubeyi kabul ediyor; manager-sız şube güvenli.

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!validateWorkingHours(workingHours)) {
      setWorkingHoursError('Açık günlerde açılış ve kapanış saati farklı olmalıdır.');
      return;
    }

    // Geriye dönük uyumluluk: ilk açık günün saatlerini tekil alanlara yansıt.
    const firstOpen = workingHours.find((wh) => !wh.closed);
    const payload: CreateBranchDto = {
      ...formData,
      branchOpeningTime: firstOpen?.openTime || formData.branchOpeningTime,
      branchClosingTime: firstOpen?.closeTime || formData.branchClosingTime,
      workingHours,
    };

    setLoading(true);
    try {
      await createBranch(payload);
      router.push('/dashboard/branches');
    } catch (error) {
      console.error('Failed to create branch:', error);
    } finally {
      setLoading(false);
    }
  }, [formData, workingHours, validateForm, router]);

  const handleCancel = useCallback(() => {
    router.push('/dashboard/branches');
  }, [router]);

  // Helper function to get role label from authorities array
  const getRoleLabel = (authorities?: string[]): string => {
    if (!authorities || authorities.length === 0) {
      return 'Kullanıcı';
    }

    // Check for highest privilege role first
    if (authorities.includes('ROLE_SUPER_ADMIN')) {
      return 'Süper Admin';
    }
    if (authorities.includes('ROLE_ADMIN')) {
      return 'Admin';
    }
    if (authorities.includes('ROLE_MANAGER')) {
      return 'Yönetici';
    }

    return 'Kullanıcı';
  };

  return (
    <DashboardContent>
      <Box display="flex" alignItems="center" mb={5}>
        <Button
          onClick={handleCancel}
          startIcon={<Iconify icon="eva:arrow-back-fill" />}
          sx={{ mr: 2 }}
        >
          Geri
        </Button>
        <Typography variant="h4">Yeni Şube Oluştur</Typography>
      </Box>

      <Card sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Şube Adı"
                value={formData.name}
                onChange={handleInputChange('name')}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.companyId}>
                <InputLabel>Firma</InputLabel>
                <Select
                  value={formData.companyId}
                  label="Firma"
                  onChange={handleSelectChange('companyId')}
                >
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.companyId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {errors.companyId}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Adres"
                multiline
                rows={3}
                value={formData.address}
                onChange={handleInputChange('address')}
                error={!!errors.address}
                helperText={errors.address}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Enlem (Latitude)"
                type="number"
                value={formData.latitude}
                onChange={handleInputChange('latitude')}
                inputProps={{ step: 'any' }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Boylam (Longitude)"
                type="number"
                value={formData.longitude}
                onChange={handleInputChange('longitude')}
                inputProps={{ step: 'any' }}
              />
            </Grid>

            <Grid item xs={12}>
              <WorkingHoursGrid
                value={workingHours}
                onChange={(v) => {
                  setWorkingHours(v);
                  if (workingHoursError) setWorkingHoursError(null);
                }}
              />
              {workingHoursError && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  {workingHoursError}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Durum</InputLabel>
                <Select
                  value={formData.status}
                  label="Durum"
                  onChange={handleSelectChange('status')}
                >
                  <MenuItem value={BranchStatus.OPEN}>Açık</MenuItem>
                  <MenuItem value={BranchStatus.CLOSED}>Kapalı</MenuItem>
                  <MenuItem value={BranchStatus.MAINTENANCE}>Bakımda</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="subtitle1">
                  Sorumlular ({formData.responsibleUsernames.length})
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Iconify icon="mingcute:add-line" />}
                  onClick={() => setEmployeeDialogOpen(true)}
                >
                  Sorumlu Ekle
                </Button>
              </Box>
              
              {errors.responsibleUsernames && (
                <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
                  {errors.responsibleUsernames}
                </Typography>
              )}

              <Box display="flex" flexWrap="wrap" gap={1}>
                {formData.responsibleUsernames.map((username) => (
                  <Chip
                    key={username}
                    label={username}
                    onDelete={() => handleRemoveResponsible(username)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={handleCancel}>
              İptal
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={loading}
              startIcon={<Iconify icon="eva:save-fill" />}
            >
              Kaydet
            </LoadingButton>
          </Stack>
        </Box>
      </Card>

      {/* Employee Selection Dialog */}
      <Dialog
        open={employeeDialogOpen}
        onClose={() => setEmployeeDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Sorumlu Çalışan Seç</DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            fullWidth
            placeholder="Çalışan ara... (ad, kullanıcı adı)"
            value={employeeSearch}
            onChange={(e) => setEmployeeSearch(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          {employeeLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Scrollbar>
              <TableContainer sx={{ overflow: 'unset' }}>
                <Table sx={{ minWidth: 500 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedEmployees.length > 0 && selectedEmployees.length < filteredEmployees.length}
                          checked={filteredEmployees.length > 0 && selectedEmployees.length === filteredEmployees.length}
                          onChange={(event) => handleSelectAllEmployees(event.target.checked)}
                        />
                      </TableCell>
                      <TableCell>Ad</TableCell>
                      <TableCell>Kullanıcı Adı</TableCell>
                      <TableCell>Rol</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredEmployees.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedEmployees.includes(user.username)}
                            onChange={() => handleEmployeeSelection(user.username)}
                          />
                        </TableCell>
                        <TableCell>
                          {user.name || user.username}
                        </TableCell>
                        <TableCell>{user.username}</TableCell>
                        <TableCell>{getRoleLabel(user.authorities)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          )}

          <TablePagination
            component="div"
            count={filteredEmployees.length}
            page={employeePage}
            onPageChange={(event, newPage) => setEmployeePage(newPage)}
            rowsPerPage={employeeRowsPerPage}
            onRowsPerPageChange={(event) => {
              setEmployeeRowsPerPage(parseInt(event.target.value, 10));
              setEmployeePage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage="Sayfa başına satır:"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmployeeDialogOpen(false)}>İptal</Button>
          <Button 
            onClick={handleAddSelectedEmployees} 
            variant="contained"
            disabled={selectedEmployees.length === 0}
          >
            Seçilenleri Ekle ({selectedEmployees.length})
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardContent>
  );
} 